import type { UserLanguage } from '@invoice/shared/schemas';
import { createFileRoute } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';

import { AuthRequiredState } from '@/components/AuthRequiredState';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useI18n } from '@/lib/i18n';
import { ApiError } from '@/lib/api';
import { useSession } from '@/lib/queries/auth';
import { useUpdateCurrentUserSettings } from '@/lib/queries/users';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const { t, roleLabel, statusLabel, setLanguage: applyLanguage } = useI18n();
  const sessionQuery = useSession();
  const updateSettingsMutation = useUpdateCurrentUserSettings();
  const [language, setLanguage] = useState<UserLanguage>('de');
  const [defaultCustomerId, setDefaultCustomerId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(
    null,
  );

  useEffect(() => {
    setLanguage(sessionQuery.data?.user.language ?? 'de');
    setDefaultCustomerId(sessionQuery.data?.user.defaultCustomerId ?? null);
    if (sessionQuery.data?.user.language) {
      applyLanguage(sessionQuery.data.user.language);
    }
  }, [sessionQuery.data?.user.defaultCustomerId, sessionQuery.data?.user.language]);

  useEffect(() => {
    const user = sessionQuery.data?.user;
    const customers = user?.customers ?? [];
    if (!user || user.role === 'admin' || customers.length === 0) return;

    const selectedStillVisible =
      defaultCustomerId !== null && customers.some((customer) => customer.id === defaultCustomerId);
    if (selectedStillVisible) return;

    const sessionDefault = user.defaultCustomerId;
    const visibleSessionDefault =
      sessionDefault !== null
        ? customers.find((customer) => customer.id === sessionDefault)?.id ?? null
        : null;
    setDefaultCustomerId(visibleSessionDefault ?? customers[0]?.id ?? null);
  }, [defaultCustomerId, sessionQuery.data?.user]);

  const isUnauthorized =
    sessionQuery.error instanceof ApiError && sessionQuery.error.status === 401;

  if (isUnauthorized) {
    return <AuthRequiredState message={t('settings.unauthorized')} />;
  }

  if (sessionQuery.isPending) {
    return (
      <section className="flex flex-col gap-6">
        <PageHeader />
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            {t('settings.loading')}
          </CardContent>
        </Card>
      </section>
    );
  }

  if (sessionQuery.isError) {
    return (
      <section className="flex flex-col gap-6">
        <PageHeader />
        <Card>
          <CardContent className="pt-6 text-sm text-destructive">
            {sessionQuery.error.message}
          </CardContent>
        </Card>
      </section>
    );
  }

  const user = sessionQuery.data.user;
  const customers = user.customers ?? [];
  const showDefaultTenantField = user.role !== 'admin';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);

    try {
      const nextSession = await updateSettingsMutation.mutateAsync({
        language,
        defaultCustomerId: showDefaultTenantField ? defaultCustomerId : null,
      });
      applyLanguage(nextSession.user.language);
      setLanguage(nextSession.user.language);
      setDefaultCustomerId(nextSession.user.defaultCustomerId ?? null);
      setFeedback({ tone: 'success', message: t('settings.saveSuccess') });
    } catch (error) {
      setFeedback({
        tone: 'error',
        message: error instanceof Error ? error.message : t('settings.saveFailed'),
      });
    }
  };

  return (
    <section className="flex flex-col gap-6">
      <PageHeader />

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.accountTitle')}</CardTitle>
          <CardDescription>{t('settings.accountDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 sm:grid-cols-2">
          <InfoField label={t('common.name')} value={user.name ?? '-'} />
          <InfoField label={t('common.email')} value={user.email} />
          <InfoField label={t('common.role')} value={roleLabel(user.role)} />
          <InfoField label={t('common.status')} value={statusLabel(user.status)} />
        </CardContent>
      </Card>

      <Card>
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle>{t('settings.languageTitle')}</CardTitle>
            <CardDescription>{t('settings.languageDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-language">{t('settings.languageLabel')}</Label>
              <Select
                id="settings-language"
                value={language}
                onChange={(event) => setLanguage(event.target.value as UserLanguage)}
                disabled={updateSettingsMutation.isPending}
                data-testid="settings-language"
              >
                <option value="de">{t('settings.languageOption.de')}</option>
                <option value="en">{t('settings.languageOption.en')}</option>
              </Select>
            </div>

            <div className="flex flex-col gap-3 border-t border-border/60 pt-4">
              <div className="flex flex-col gap-1">
                <h2 className="text-sm font-semibold text-foreground">
                  {t('settings.defaultTenantTitle')}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t('settings.defaultTenantDescription')}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="settings-default-customer">
                  {t('settings.defaultTenantLabel')}
                </Label>
              {showDefaultTenantField ? (
                <>
                  <Select
                    id="settings-default-customer"
                    value={defaultCustomerId ?? ''}
                    onChange={(event) =>
                      setDefaultCustomerId(event.target.value.length > 0 ? event.target.value : null)
                    }
                    disabled={updateSettingsMutation.isPending}
                    data-testid="settings-default-customer"
                  >
                    {customers.length === 0 ? (
                      <option value="">{t('settings.defaultTenantEmpty')}</option>
                    ) : null}
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </Select>
                </>
              ) : (
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="settings-default-customer-admin-hint"
                >
                  {t('settings.defaultTenantAdminHint')}
                </p>
              )}
            </div>
            </div>

            {feedback ? (
              <output
                className={
                  feedback.tone === 'success'
                    ? 'rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700'
                    : 'rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive'
                }
                data-testid="settings-feedback"
              >
                {feedback.tone === 'success' ? t('settings.saveSuccess') : feedback.message}
              </output>
            ) : null}
          </CardContent>
          <CardFooter className="justify-end">
            <Button
              type="submit"
              disabled={updateSettingsMutation.isPending || sessionQuery.isPending}
              data-testid="settings-save"
            >
              {updateSettingsMutation.isPending ? t('settings.saving') : t('settings.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </section>
  );
}

function PageHeader() {
  const { t } = useI18n();

  return (
    <header className="flex flex-col gap-1">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
        {t('settings.sectionLabel')}
      </p>
      <h1 className="text-3xl font-semibold tracking-tight text-foreground">
        {t('settings.pageTitle')}
      </h1>
      <p className="max-w-2xl text-sm text-muted-foreground">{t('settings.pageDescription')}</p>
    </header>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}
